/*
 * SHELL · Historia 3D cinematográfica — yanipaper
 * ================================================
 * TÚNEL = FOOTAGE FOTO-REAL SCRUBBEADO POR SCROLL (2026-07-09, giro de dirección de Héctor).
 * Se ABANDONA el túnel procedural de Three.js (racks/núcleo/nodos instanciados) — nunca alcanzó el
 * fotorrealismo pedido y el render propio topaba con el hardware del nodo (CPU + Intel HD 630). En su
 * lugar, el corredor es un VIDEO real (data-center ciberpunk, primera persona), extraído a 240 frames
 * (public/tunnel-frames-real/ + manifest) y dibujado en el #cinema como secuencia scrubbeada: el
 * progreso de scroll 0→1 mapea al frame 1→240. Ver src/tunnel-scrub.js.
 *
 * Fuente del footage: docs/muestra_una_animación_en_bucle.mp4 (loop). La marca de agua ✦ del generador
 * se removió por inpaint de difusión (originales en blender/frames-real-orig/); el scrub remata con una
 * viñeta suave en la esquina inferior-derecha.
 *
 * CONSERVADO INTACTO de la versión previa (la costura DOM/scroll no cambió):
 *   - Gate "Entrar" + intro cinemática (cumbia + glifos + wordmark). Ver src/intro.js.
 *   - Copy de los 11 beats (voz A) en index.html — sin cambios.
 *   - Constelación DOM (beat 10) desde el feed CMS filtrado + `sensible:true` (datos y runtime) +
 *     assertSinSensibles + test. Es el contenido real del Acto III (los nodos 3D eran placeholder).
 *   - Scrim WCAG, HALL-A, gsap.matchMedia, Lenis + ScrollTrigger y fallback móvil/reduced-motion.
 *
 * QUÉ SE PIERDE respecto al placeholder (eran gestos 3D no-reales, portafolio ≠ testimonial): el gesto
 * abstracto del borrado del Acto II y la constelación de nodos EN WEBGL. La narrativa de texto y la
 * constelación DOM siguen contando los 3 actos.
 */

import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Lenis from 'lenis';

import { startIntro } from './intro.js';
import { createTunnelScrub } from './tunnel-scrub.js';
import { createGateGlitch } from './gate-glitch.js';
import { renderPortafolio, setupPisosHorizontal } from './portafolio.js';
import { mountTextEffects } from './text-effects.js';
import { mountSiteNav } from './nav.js';
import { mountTunnelVideoScrub } from './tunnel-video.js';

gsap.registerPlugin(ScrollTrigger);

// Reload a mitad de scroll: el gate/intro son `fixed`; si el navegador restaura el scroll previo, el
// gate queda flotando sobre una página ya desplazada. Tomamos control del restore y volvemos al tope.
if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
window.scrollTo(0, 0);

/* ------------------------------------------------------------------ */
/* Accesibilidad: matchMedia gobierna TODO el montaje del 3D (E0)     */
/* ------------------------------------------------------------------ */
const CINEMA_QUERY = '(min-width: 769px) and (pointer: fine) and (prefers-reduced-motion: no-preference)';

// --- ENTRADA: pantalla "Entrar" → cumbia + intro (glifos + wordmark) → túnel ---
// El clic en "Entrar" desbloquea el audio (autoplay) y arranca la cumbia + el intro. Se lanza ANTES
// de montar el 3D para ser robusta ante fallos del WebGL. Sin cinema (móvil/reduced) se salta (HALL-A).
const cinemaOK = window.matchMedia(CINEMA_QUERY).matches;
// El INTRO (gate + intro musical) corre en TODOS los dispositivos salvo prefers-reduced-motion; el
// túnel de escritorio (frames) sigue atado a cinemaOK, pero móvil/tablet tienen su propio túnel de video.
const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// VOLVER DESDE UN POST (flecha ← de /blog/<slug>.html, href="/"): NO repetir la pantalla "Entrar";
// caer directo al home (hero + notas). Se detecta por el referrer del MISMO origen apuntando a /blog/.
// OJO: `document.referrer` PERSISTE entre RECARGAS → hay que exigir además que la navegación sea
// genuina (`navigate`, no `reload`), o el gate no volvería a salir tras haber visitado un post una vez.
let fromBlog = false;
try {
  const nav = performance.getEntriesByType('navigation')[0];
  const navType = nav ? nav.type : 'navigate';
  if (navType === 'navigate' && document.referrer) {
    const r = new URL(document.referrer);
    fromBlog = r.origin === window.location.origin && r.pathname.includes('/blog/');
  }
} catch { /* sin Performance API o referrer opaco → flujo normal con gate */ }

// Navegación mínima (hamburguesa → overlay). Se MUESTRA cuando main.js pone `body.nav-ready` (al cerrar
// el intro / o de inmediato en móvil-reduced-skip). El scroll de las anclas usa Lenis si el cinema está
// montado (`cinemaLenis`), o `scrollIntoView` nativo si no. Ver src/nav.js.
let cinemaLenis = null;
mountSiteNav({
  scrollTo: (el) => {
    if (cinemaLenis) cinemaLenis.scrollTo(el, { duration: 1.2, offset: 0 });
    else el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  },
});

// `onDone` se dispara cuando el intro cierra (o al instante en reduced/skip): revela la navegación.
// El intro (Entrar → intro musical → home) sale en móvil/tablet también; solo lo salta reduced-motion.
startIntro({
  reducedMotion: prefersReduced,
  skipEntry: fromBlog,
  onDone: () => document.body.classList.add('nav-ready'),
});

// Glitch de la pantalla "Entrar": solo cuando el gate existe (modo cinema y NO se volvió de un post).
// Se autodestruye cuando el gate sale (is-out / fuera del DOM). Ver src/gate-glitch.js.
if (cinemaOK && !fromBlog) {
  const gateCanvas = document.getElementById('gate-canvas');
  if (gateCanvas) createGateGlitch({ canvas: gateCanvas });
}

// TÚNEL DE FONDO EN MÓVIL/TABLET: donde NO corre el cinema de escritorio (frames) y no hay reduced-motion,
// se monta el <video> scrubbeado por scroll. Desktop nunca descarga el mp4. Ver src/tunnel-video.js.
if (!cinemaOK && !prefersReduced) {
  mountTunnelVideoScrub({ url: `${import.meta.env.BASE_URL}video/tunnel.mp4` });
}

// El PORTAFOLIO (Acto III) se pinta SIEMPRE (HALL-A: legible sin WebGL/scroll). El scroll
// horizontal por pisos es realce de escritorio (se monta dentro de la rama cinema). Ver src/portafolio.js.
renderPortafolio(document.querySelector('#portafolio-pisos'));

// Efectos de texto que envuelven el DOM (Ransom-Note en "Mi trabajo" + Teletype en la cita).
// Se montan SIEMPRE (HALL-A): internamente respetan reduced-motion. Ver src/text-effects.js.
mountTextEffects();

const mm = gsap.matchMedia();

mm.add({ cinema: CINEMA_QUERY }, (ctx) => {
  if (!ctx.conditions.cinema) return; // rama fallback: no monta nada (HALL-A: HTML ya legible)
  const teardownCinema = mountCinema();
  // Pisos horizontales (pin + scrub, uno por sección): en móvil/reduced el CSS los deja verticales.
  const teardownPisos = setupPisosHorizontal(document.querySelector('#portafolio-pisos'));
  return () => { teardownPisos(); teardownCinema && teardownCinema(); };
});

// Reveals de texto: solo con cinema (sin esto el texto queda pleno = HALL-A).
if (window.matchMedia(CINEMA_QUERY).matches) {
  mountReveals();
}

/* ================================================================== */
/* CINEMA · scrub del túnel real (footage) por scroll                 */
/* ================================================================== */
/*
 * Dibuja la secuencia de frames reales en el #cinema mapeando el progreso de scroll (0→1) al frame
 * (1→N). Toda la costura de scroll (Lenis + ScrollTrigger), el re-encendido del nombre y el ciclo de
 * vida (montaje/limpieza por media query) se conserva de la versión previa; solo cambió QUÉ se dibuja:
 * antes una escena WebGL, ahora un canvas 2D con el footage. Ver src/tunnel-scrub.js.
 */
function mountCinema() {
  const canvas = document.querySelector('#cinema');
  const scrub = createTunnelScrub({ canvas });

  // --- Smooth scroll (Lenis) + wiring GSAP ---
  const lenis = new Lenis({ lerp: 0.1 });
  cinemaLenis = lenis; // expone Lenis al nav para el scroll suave de las anclas
  lenis.on('scroll', ScrollTrigger.update);
  const tickerFn = (time) => lenis.raf(time * 1000);
  gsap.ticker.add(tickerFn);
  gsap.ticker.lagSmoothing(0);
  document.documentElement.classList.add('lenis');

  // iOS Safari + scroll: contra el jitter (verificar en iOS real).
  ScrollTrigger.normalizeScroll(true);

  // --- ScrollTrigger: scroll de #story → progress 0→1 ---
  let targetProgress = 0;
  let progress = 0;
  const st = ScrollTrigger.create({
    trigger: '#story',
    start: 'top top',
    end: 'bottom bottom',
    scrub: true,
    // Los pisos del portafolio se PINEAN (setupPisosHorizontal, creado DESPUÉS) y añaden mucha altura
    // de scroll. Con prioridad por defecto, este `st` se refresca ANTES que los pines y fija su `end`
    // con la altura previa → el túnel llegaba al último frame en "MI TRABAJO" y se congelaba. Con
    // refreshPriority menor, `st` se refresca AL FINAL (tras los pines) y su `end` cubre TODO el scroll.
    refreshPriority: -1,
    onUpdate: (self) => { targetProgress = self.progress; },
  });

  // --- Loop de dibujo: lerp del progreso → frame + re-encendido del nombre ---
  let raf = 0;
  let running = true;
  function loop() {
    if (!running) return;
    // suavizado del progreso (lerp) para que el scrub no salte con el scroll
    progress += (targetProgress - progress) * 0.10;
    const p = clamp01(progress);
    scrub.drawProgress(p);
    raf = requestAnimationFrame(loop);
  }

  // Carga los frames; revela apenas el PRIMERO esté listo (la intro corre encima mientras tanto) y
  // arranca el loop. El resto de frames sigue cargando en segundo plano (drawProgress omite faltantes).
  scrub.load().then(() => {
    canvas.classList.add('is-live');
    document.body.classList.add('reveal');
    raf = requestAnimationFrame(loop);
  });

  // --- Resize ---
  const onResize = () => { scrub.resize(); ScrollTrigger.refresh(); };
  window.addEventListener('resize', onResize);

  // --- Limpieza (la llama GSAP al revertir la media query) ---
  return () => {
    running = false;
    if (raf) cancelAnimationFrame(raf);
    window.removeEventListener('resize', onResize);
    st.kill();
    ScrollTrigger.normalizeScroll(false);
    gsap.ticker.remove(tickerFn);
    lenis.destroy();
    cinemaLenis = null;
    document.documentElement.classList.remove('lenis');
    document.body.classList.remove('reveal');
    canvas.classList.remove('is-live');
  };
}


/* ================================================================== */
/* Reveals de texto                                                   */
/* ================================================================== */
function mountReveals() {
  const io = new IntersectionObserver(
    (entries) => {
      for (const e of entries) {
        if (e.isIntersecting) { e.target.classList.add('is-visible'); io.unobserve(e.target); }
      }
    },
    { threshold: 0.35 }
  );
  document.querySelectorAll('.chapter').forEach((el) => io.observe(el));
}

/* ------------------------------------------------------------------ */
/* util                                                               */
/* ------------------------------------------------------------------ */
function clamp01(v) { return v < 0 ? 0 : v > 1 ? 1 : v; }
